import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { ReportColumnType } from '../../../atoms/additionalColumns';
import { ACalculatedFields, CompanyCalculatedField, getCalculatedFieldsByCompany } from '../../../atoms/calculatedFields';
import { AProducts } from '../../../atoms/product';
import InputSearch from '../../../components_v2/input/InputSearch';
import { BorderColor, LightGreySidely } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { ValueInnerToNode } from '../../reports/interpretor/ReportInterpretor';

export type ProductKpis = { uuid: string, name: string, fields: Record<string, ReportColumnType> };

const KpiTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
	th, td {
		text-align: left;
		padding: 8px 12px;
		border-bottom: 1px solid ${BorderColor};
		white-space: nowrap;
	}
	th {
		position: sticky;
		top: 0;
		background-color: ${LightGreySidely};
		font-weight: 500;
	}
`;

// Les valeurs par produit (champs company_product et KPI externes par
// produit) stockent une map {"products": [[uuid produit, valeur], ...]} par
// société: regroupée ici en lignes produit
export function useCompanyProductKpis(clientCompanyId: number): { products: ProductKpis[], fieldNames: string[] } {
	const productsAtom = useRecoilValue(AProducts);
	const calculatedFields = useRecoilValue(ACalculatedFields);
	const [values, setValues] = React.useState<CompanyCalculatedField[]>();

	React.useEffect(() => {
		getCalculatedFieldsByCompany(clientCompanyId).then(setValues);
	}, [clientCompanyId]);

	return React.useMemo(() => {
		const map = new Map<string, ProductKpis>();
		const fieldNames = new Set<string>();
		values?.forEach(([fieldId, value]) => {
			const def = calculatedFields.find(c => c.id === fieldId);
			// la forme de la valeur fait foi, quelle que soit la cible
			const entries = (value as { products?: [string, ReportColumnType][] })?.products;
			if (!def || !entries) return;
			fieldNames.add(def.field_name);
			entries.forEach(([uuid, v]) => {
				const kpis = map.get(uuid) ?? { uuid, name: productsAtom.find(p => p.uuid === uuid)?.name ?? uuid, fields: {} };
				kpis.fields[def.field_name] = v;
				map.set(uuid, kpis);
			});
		});
		return {
			products: [...map.values()].sort((a, b) => a.name.localeCompare(b.name)),
			fieldNames: [...fieldNames].sort((a, b) => a.localeCompare(b)),
		};
	}, [values, calculatedFields, productsAtom]);
}

export default function ProductsKpis(props: { products: ProductKpis[], fieldNames: string[] }): JSX.Element {
	const [search, setSearch] = React.useState('');
	const filtered = props.products.filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase()));

	return <div style={{ padding: '0 15px 15px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
		<div style={{ maxWidth: '260px' }}>
			<InputSearch
				placeholder={translateToString('search')}
				name='search_product_kpis'
				type='text'
				onChange={(value: string) => setSearch(value)}
			/>
		</div>
		<div style={{ overflow: 'auto' }}>
			<KpiTable>
				<thead>
					<tr>
						<th><Translate id='products.title' /></th>
						{props.fieldNames.map(name => <th key={name}>{name}</th>)}
					</tr>
				</thead>
				<tbody>
					{filtered.map(product => <tr key={product.uuid}>
						<td>{product.name}</td>
						{props.fieldNames.map(name => <td key={name}>
							{name in product.fields ? <ValueInnerToNode value={product.fields[name]} /> : '-'}
						</td>)}
					</tr>)}
				</tbody>
			</KpiTable>
		</div>
	</div>;
}
